import { useEffect, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";
import { useIntl } from "react-intl";
import { Button } from "./Button";

/**
 * Horizontally scrollable card row with page dots derived from its rendered width.
 * The row moves by trackpad, Shift+wheel, the dots, the chevrons beside them and, once
 * the pointer has been moved onto it, the mouse wheel; every path keeps the active page
 * in sync.
 */
export function HorizontalCardCarousel({ children }: { children: ReactNode }) {
  const intl = useIntl();
  const areaRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  useWheelOnceAimedAt(ref);
  useHeightHeldWhileInside(areaRef, ref);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let frame: number | undefined;
    const update = () => {
      const width = el.clientWidth || 1;
      const maxScroll = el.scrollWidth - width;
      // A partially visible final page still needs its own dot.
      const count = maxScroll > 4 ? Math.max(1, Math.ceil(el.scrollWidth / width)) : 1;
      setPageCount(count);
      setPage(maxScroll > 0 ? Math.round((el.scrollLeft / maxScroll) * (count - 1)) : 0);
    };
    const scheduleUpdate = () => {
      if (frame !== undefined) return;
      frame = requestAnimationFrame(() => {
        frame = undefined;
        update();
      });
    };

    update();
    el.addEventListener("scroll", scheduleUpdate, { passive: true });

    // The scroll container keeps its own width while its content changes: the first
    // render happens before the query resolves, so measuring only the container
    // misses every card that arrives later and the dots stay hidden until the first
    // scroll event. Therefore the cards are observed as well, and childList changes
    // re-subscribe to whatever set of cards is currently mounted.
    //
    // Ignore height-only notifications: ShareItemCardCover animates its own height on
    // hover (grid-rows 0fr -> 1fr over 500ms), which fires this observer on every frame
    // of that transition even though pagination only depends on horizontal geometry.
    // Reading scroll geometry in update() forces a layout recalc, and doing that every
    // frame competes with the hover transition (confirmed via Safari Timelines: Layout
    // and Recalculate Styles firing in lockstep with pointermove while just hovering a
    // card, not once per hover as a pure CSS transition would). Track each observed
    // target's last known width and only schedule an update when it actually changed.
    const lastWidths = new WeakMap<Element, number>();
    const resizeObserver = new ResizeObserver((entries) => {
      let widthChanged = false;
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (lastWidths.get(entry.target) !== width) {
          lastWidths.set(entry.target, width);
          widthChanged = true;
        }
      }
      if (widthChanged) scheduleUpdate();
    });
    resizeObserver.observe(el);
    const observeCards = () => {
      for (const card of Array.from(el.children)) resizeObserver.observe(card);
    };
    observeCards();
    const mutationObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const removed of Array.from(record.removedNodes)) {
          if (removed instanceof Element) resizeObserver.unobserve(removed);
        }
      }
      observeCards();
      scheduleUpdate();
    });
    mutationObserver.observe(el, { childList: true });

    return () => {
      el.removeEventListener("scroll", scheduleUpdate);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, []);

  const scrollToPage = (index: number) => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ left: (index / (pageCount - 1)) * (el.scrollWidth - el.clientWidth), behavior: "smooth" });
  };

  return (
    <div ref={areaRef}>
      <div
        ref={ref}
        className="flex items-start gap-4 overflow-x-auto overscroll-x-contain pb-1 transition-[min-height] duration-500 ease-out [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
      {pageCount > 1 ? (
        // The row keeps the dots' height; the chevrons' larger hit area overflows it centred,
        // so adding them does not push the content below.
        <div className="mt-2 flex h-1.5 items-center justify-center gap-1.5">
          <Button
            variant="icon"
            size="sm"
            disabled={page === 0}
            onClick={() => scrollToPage(page - 1)}
            aria-label={intl.formatMessage({ id: "home.carouselPrevious", defaultMessage: "Previous" })}
          >
            <span aria-hidden="true">‹</span>
          </Button>
          {Array.from({ length: pageCount }, (_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => scrollToPage(index)}
              aria-label={intl.formatMessage(
                { id: "home.carouselSlide", defaultMessage: "Slide {number}" },
                { number: index + 1 },
              )}
              className={`h-1.5 rounded-full transition-all ${
                index === page ? "w-5 bg-accent" : "w-1.5 bg-ink/20 hover:bg-ink/40"
              }`}
            />
          ))}
          <Button
            variant="icon"
            size="sm"
            disabled={page === pageCount - 1}
            onClick={() => scrollToPage(page + 1)}
            aria-label={intl.formatMessage({ id: "home.carouselNext", defaultMessage: "Next" })}
          >
            <span aria-hidden="true">›</span>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Keeps the row from shrinking while the pointer is inside the carousel. Cards open a
 * panel on hover (ShareItemCardCover), so moving from one card to the next closes the
 * first while the second opens: the row would dip and pull everything below it up and
 * down. Measuring when a card is left, or when the row scrolls under a resting pointer,
 * still sees the open card at full height. Leaving the carousel hands min-height back to
 * the track's class transition, which uses the cards' own duration and curve.
 */
function useHeightHeldWhileInside(
  areaRef: RefObject<HTMLElement | null>,
  trackRef: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    const area = areaRef.current;
    const track = trackRef.current;
    if (!area || !track) return undefined;
    let inside = false;
    let hoveredCard: Element | undefined;
    let held = 0;

    const hold = () => {
      const height = track.getBoundingClientRect().height;
      if (height <= held) return;
      held = height;
      track.style.transition = "none";
      track.style.minHeight = `${height}px`;
    };
    const cardUnder = (target: EventTarget | null) =>
      target instanceof Node
        ? Array.from(track.children).find((card) => card.contains(target))
        : undefined;

    const onOver = (event: PointerEvent) => {
      // Touch has no hover, so there is nothing to hold.
      if (event.pointerType === "touch") return;
      inside = true;
      const card = cardUnder(event.target);
      if (card === hoveredCard) return;
      if (hoveredCard) hold();
      hoveredCard = card;
    };
    const onLeave = () => {
      inside = false;
      hoveredCard = undefined;
      if (!held) return;
      held = 0;
      track.style.transition = "";
      track.style.minHeight = "0px";
    };
    const onScroll = () => {
      if (inside) hold();
    };

    area.addEventListener("pointerover", onOver);
    area.addEventListener("pointerleave", onLeave);
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      area.removeEventListener("pointerover", onOver);
      area.removeEventListener("pointerleave", onLeave);
      track.removeEventListener("scroll", onScroll);
    };
  }, [areaRef, trackRef]);
}

// Pointer travel on the row that counts as aiming at it rather than a hand at rest.
const AIM_PX = 6;
// A pause longer than this between wheel events starts a new gesture. Wheel notches and
// momentum arrive far closer together; a deliberate second push comes after longer.
const GESTURE_GAP_MS = 400;

/**
 * Turns vertical wheel steps into horizontal movement, but only after the visitor has
 * moved the pointer onto the row. A row that slides under a resting pointer while the
 * page scrolls must not catch the wheel: that is exactly how scrolling down the page used
 * to get stuck. Travel is measured from clientX/Y rather than counted per pointermove,
 * because a browser may dispatch pointer events after a scroll without the hand moving.
 * A gesture that reaches either end of the row stops there instead of carrying the page
 * on (the latching browsers apply to nested scrollers); only a fresh gesture at the end
 * goes to the page, and any page scroll disarms.
 */
function useWheelOnceAimedAt(trackRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    let aimed = false;
    let lastRowWheelAt = Number.NEGATIVE_INFINITY;
    let travelled = 0;
    let last: { x: number; y: number } | undefined;

    const disarm = () => {
      aimed = false;
      travelled = 0;
      last = undefined;
    };
    const onMove = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      if (last) travelled += Math.hypot(event.clientX - last.x, event.clientY - last.y);
      last = { x: event.clientX, y: event.clientY };
      if (travelled >= AIM_PX) aimed = true;
    };
    const onWheel = (event: WheelEvent) => {
      if (!aimed || event.shiftKey || event.ctrlKey) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const max = track.scrollWidth - track.clientWidth;
      const atStart = event.deltaY < 0 && track.scrollLeft <= 0;
      const atEnd = event.deltaY > 0 && track.scrollLeft >= max - 1;
      if (max <= 0) return;
      const sameGesture = event.timeStamp - lastRowWheelAt < GESTURE_GAP_MS;
      if ((atStart || atEnd) && !sameGesture) return;
      if (!atStart && !atEnd) track.scrollLeft += event.deltaY;
      lastRowWheelAt = event.timeStamp;
      event.preventDefault();
    };
    // Scroll does not bubble, but it can be captured: any scroller that contains the row
    // (the window or the app's scroll viewport) moving means the page took the wheel.
    const onPageScroll = (event: Event) => {
      const target = event.target;
      if (target === document || (target instanceof Node && target !== track && target.contains(track))) {
        disarm();
      }
    };

    track.addEventListener("pointermove", onMove);
    track.addEventListener("pointerleave", disarm);
    track.addEventListener("wheel", onWheel, { passive: false });
    document.addEventListener("scroll", onPageScroll, { capture: true, passive: true });
    return () => {
      track.removeEventListener("pointermove", onMove);
      track.removeEventListener("pointerleave", disarm);
      track.removeEventListener("wheel", onWheel);
      document.removeEventListener("scroll", onPageScroll, { capture: true });
    };
  }, [trackRef]);
}
